import { HearOAEHero } from "@/components/pages/hearoae/HearOAEHero/HearOAEHero";
import { Layout } from "layouts";
import styles from "@/components/pages/hearoae/hearoae.module.scss";
import { FeaturesGrid } from "@/components/pages/hearoae/FeaturesGrid/FeaturesGrid";
import { Card } from "@/components/pages/hearoae/Card/Card";
import { UseCases } from "@/components/pages/hearoae/UseCases/UseCases";
import classNames from "classnames";
import { Carousel } from "@/components/pages/hearoae/Carousel/Carousel";
import Image from "next/image";
import YouTube from "react-youtube";
import PopupVideo from "@/components/popup-video";
import { ReactNode, useEffect, useState } from "react";
import { RequestDemoForm } from "@/components/forms";
import { PopupModal } from "react-calendly";
import { FormPopup } from "@/components/pages/hearoae/FormPopup/FormPopup";
import { disclaimer } from "../components/home/get-in-touch/styles/index";
import BlackFridayPromoPopup from "@/components/black-friday-promo-popup/black-friday-promo-popup";
import Head from "next/head";

const CALENDLY_URL = `https://calendly.com/d/2cs-rrm-978/hearx-group-product-demo?hide_gdpr_banner=1&primary_color=e98e6f`;

const VIDEO_ID = "-6-AGoxnBSY";

const HearOaePage: React.FC = () => {
  const [detailsSubmitted, setDetailsSubmitted] = useState(false);
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [popupState, setPopupState] = useState<{
    open: boolean;
    element: HTMLElement | null;
  }>({
    open: false,
    element: null,
  });

  const downloadDataSheetFormState = useState<boolean | undefined>(false);
  const toggleDataSheetForm = () => {
    downloadDataSheetFormState[1](!downloadDataSheetFormState[0]);
  };
  const downloadBrochureFormState = useState<boolean | undefined>(false);
  const toggleBrochureForm = () => {
    downloadBrochureFormState[1](!downloadBrochureFormState[0]);
  };

  const openNewTab = (url: string) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_blank";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  useEffect(() => {
    if (document) {
      setPopupState({
        ...popupState,
        element: document.getElementById("__next"),
      });
    }
  }, []);
  return (
    <Layout page="hearoae">
      <Head>
        <link
          rel="preload"
          href="/assets/images/black-friday-promo-popup-background.svg"
          as="image"
        />
      </Head>
      <div className={styles.root}>
        <HearOAEHero />
        <section>
          <h2>
            DISCOVER THE LATEST INNOVATION IN&nbsp;HEARING&nbsp;HEALTH TECH
          </h2>
          <p className={styles.body}>
            hearX<sup>®</sup> is proud to launch hearOAE - a groundbreaking
            smartphone and tablet-based otoacoustic emissions device with
            Bluetooth<sup>®</sup> functionality. Make hearing assessments more
            efficient and put quality patient care at the center of what you do.
            hearOAE offers fully-customizable{" "}
            <b>DP and TE screening and diagnostic functionality</b>.
          </p>
          <p className={styles.body}>
            Starting from $1,600, hearOAE provides intuitive, precise testing at
            a competitive rate.
          </p>
          <div className={styles.buttons}>
            <button onClick={toggleDataSheetForm}>
              {/* <a
                href="/assets/documents/hearOAE/CUS_hearOAE_Datasheet_04042024 FINAL.pdf"
                target="_blank"
                rel="noreferrer noopener"
              > */}
              Download Data Sheet
              {/* </a> */}
            </button>
            <button onClick={toggleBrochureForm}>
              {/* <a
                href="/assets/documents/hearOAE/CUS_hearOAE_Brochure_04042024.pdf"
                target="_blank"
                rel="noreferrer noopener"
              > */}
              Download Brochure and Pricing
              {/* </a> */}
            </button>
          </div>
        </section>
        <section>
          <h2>LEAD THE WAY IN OTOACOUSTIC EMISSIONS TESTING</h2>
          <p className={classNames(styles.body, styles.center)}>
            Features and benefits for precision OAE testing
          </p>
          <FeaturesGrid />
          <div className={classNames(styles.buttons, styles.center)}>
            <button
              onClick={() => {
                setPopupState({ ...popupState, open: true });
              }}
            >
              Book a Free Demo
            </button>
          </div>
        </section>
        <section>
          <h2>STREAMLINE YOUR OAE TESTING WITH A NEXTGEN SOLUTION</h2>
          <div className={styles.cards}>
            <Card
              background="/assets/images/pages/hearoae/solutions/solutions-1.png"
              title="Accurate, intuitive OAE testing"
            >
              By offering Transient Evoked (TEOAE) and Distortion Product
              (DPOAE) testing capabilities in one device, hearOAE provides
              effortless access to accurate insights. Its intuitive app design,
              simple navigation, and clear test results enables you to deliver
              exceptional patient care.
            </Card>
            <Card
              background="/assets/images/pages/hearoae/solutions/solutions-2.png"
              title="Comprehensive, cost-effective hearing technology"
            >
              hearOAE is an automated and cost-effective solution suitable for
              testing different age groups including newborns, children and
              adults.
            </Card>
            <Card
              background="/assets/images/pages/hearoae/solutions/solutions-3.png"
              title="Accessible and versatile"
            >
              Customizable protocols deliver reliable testing. Tailored for
              smartphones and tablets, hearOAE is perfect for hearing healthcare
              professionals in clinics, community settings, and beyond.
            </Card>
          </div>
        </section>
        <div className={styles.dark}>
          <section>
            <h2>IDEAL FOR THE FOLLOWING USE CASES</h2>
            <Carousel
              settings={{
                responsive: [
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: 3,
                      slidesToScroll: 3,
                    },
                  },
                ],
              }}
              slides={[
                <div className={styles.useCaseSlide}>
                  <h3>Newborn and clinical screening</h3>
                  <div>
                    Designed for efficient, accurate screening across various
                    age groups.
                  </div>
                </div>,
                <div className={styles.useCaseSlide}>
                  <h3>Clinical trials</h3>
                  <div>
                    Clinically-validated data collection to help you gain deeper
                    insights into hearing health.
                  </div>
                </div>,
                <div className={styles.useCaseSlide}>
                  <h3>School screening programs</h3>
                  <div>
                    Affordable solution for large-scale screening initiatives.
                  </div>
                </div>,
                <div className={styles.useCaseSlide}>
                  <h3>Research projects</h3>
                  <div>Adaptable protocols with reliable results</div>
                </div>,
                <div className={styles.useCaseSlide}>
                  <h3>Work place screening</h3>
                  <div>
                    Screen employees for hearing loss in occupational or
                    recreational settings to promote proactive hearing health
                    management.
                  </div>
                </div>,
              ]}
            />
          </section>
        </div>
        <section>
          <h2>WHAT'S IN THE BOX</h2>
          <div className={classNames(styles.body, styles.center)}>
            All device components are stored in a single carry case, to ensure
            convenience and portability.
          </div>
          <Carousel
            type="fade"
            settings={{
              transitionDuration: 1000,
              autoplay: true,
              duration: 5000,
              easing: "ease-in",
            }}
            slides={[
              <WhatsInTheBoxSlide
                image="/assets/images/pages/hearoae/whats-in-the-box/p/smart-device.png"
                title="Smart Device"
                content={
                  <div>
                    Samsung smart device (including charge adapter and USB-C
                    charging cable)
                  </div>
                }
              />,

              <WhatsInTheBoxSlide
                image="/assets/images/pages/hearoae/whats-in-the-box/p/ear-tips.png"
                title="Ear Tips"
                content={
                  <div>
                    <div className="mb-4">Sanibel ADI assortment box</div>
                    <div>
                      <div className={styles.note}>
                        <div>Please note:</div>
                        <div>
                          Only use the Sanibel ADI series of ear tips. Different
                          colors and shapes represent different sizes. Ear tips
                          are for single patient use.
                        </div>
                      </div>
                    </div>
                  </div>
                }
              />,
              <WhatsInTheBoxSlide
                image="/assets/images/pages/hearoae/whats-in-the-box/p/accessories.png"
                title="Accessories"
                content={
                  <div>
                    <ul>
                      <li>Cleaning line</li>
                      <li>Cable Clip</li>
                      <li>Printed Quick Start Guide</li>
                    </ul>
                    <div>
                      <div className={styles.note}>
                        <div>Please note:</div>
                        <div>Cleaning lines are for single patient use.</div>
                      </div>
                    </div>
                  </div>
                }
              />,
              <WhatsInTheBoxSlide
                image="/assets/images/pages/hearoae/whats-in-the-box/p/hardcover-carry-case.png"
                title="Hardcover carry case"
              />,
              <WhatsInTheBoxSlide
                image="/assets/images/pages/hearoae/whats-in-the-box/p/probe-couplers.png"
                title="5 x probe couplers"
                content={
                  <div>
                    <div>
                      There are 5 probe couplers included in your starter kit.
                    </div>
                  </div>
                }
              />,
              <WhatsInTheBoxSlide
                image="/assets/images/pages/hearoae/whats-in-the-box/p/hearoae-device.png"
                title="hearOAE device"
                content={
                  <div>
                    <div>
                      The same charging cable can be used for the smart device.
                    </div>
                  </div>
                }
              />,
              <WhatsInTheBoxSlide
                image="/assets/images/pages/hearoae/whats-in-the-box/p/hearoae-probe.png"
                title="hearOAE probe"
              />,
            ]}
          />
          <div className={classNames(styles.buttons, styles.center)}>
            <button
              onClick={() => {
                setPopupState({ ...popupState, open: true });
              }}
            >
              Book a free demo
            </button>
            <PopupModal
              url={CALENDLY_URL}
              open={popupState.open}
              onModalClose={() => {
                setPopupState({ ...popupState, open: false });
              }}
              rootElement={popupState.element as HTMLElement}
            />
          </div>
        </section>
        <section className={styles.videoSection}>
          <h2>FIND OUT HOW THE OAE LANDSCAPE IS CHANGING</h2>
          <YouTube
            containerClassName={styles.video}
            id={VIDEO_ID}
            opts={playerOptions}
          />
          {showVideoPopup && (
            <PopupVideo
              youtubeId={VIDEO_ID}
              closeHandler={() => setShowVideoPopup(false)}
              options={{
                playerVars: {
                  controls: 1,
                },
              }}
            />
          )}
          <div className={styles.buttons}>
            <button onClick={() => setShowVideoPopup(true)}>Watch Video</button>
          </div>
        </section>
        <div className={styles.dark}>
          <section>
            <h2>TAKE DATA MANAGEMENT TO THE NEXT LEVEL WITH MHEALTH STUDIO</h2>
            <div className={styles.cols2}>
              <div>
                <p className={styles.body}>
                  hearOAE is launched from mHealth Studio, which allows seamless
                  synchronization between hearOAE on your hearX smart device and
                  the mHealth Studio Cloud (EHR Platform) in the cloud.
                </p>
                <p className={styles.body}>
                  This ensures that all test and patient records are accessible
                  from the cloud. mHealth Studio is included with your purchase
                  of any hearX<sup>®</sup> software. It allows for data
                  capturing, management, surveillance, referrals, report
                  generation and secure usage anywhere, anytime.
                </p>
              </div>
              <Image
                src="/assets/images/pages/hearoae/mhealth.png"
                width={573}
                height={319}
                objectFit="contain"
              />
            </div>
          </section>
        </div>
        <section>
          <h2>RESEARCH PUBLICATIONS THAT FEATURE HEAROAE</h2>
          <div>
            <Publication
              href="https://www.sciencedirect.com/science/article/pii/S0165587624000168"
              image={{
                src: "/assets/images/pages/hearoae/publication.png",
                width: 81,
                height: 108,
              }}
              title="Neonatal hearing screening using a smartphone-based otoacoustic emission device: A comparative study"
              text="Andani Gluggy Madzivhandila, Talita le Roux, Leigh Biagio de Jager International Journal of Pediatric Otorhinolaryngology 2024"
            />
          </div>
        </section>
        <section>
          <div className={styles.textCard}>
            <p className={styles.body}>
              We believe in making{" "}
              <span>healthy hearing accessible and affordable to all.</span>{" "}
              Join us as we continue to advance and break barriers in OAE
              testing.
            </p>
          </div>
        </section>
        <div className={styles.dark}>
          <section>
            <RequestDemoForm
              translationPrefix="hearoae"
              formClassName="GA4-LeadConversion"
              formId="hearoae-get-in-touch-form"
              themeColor={{
                color: "text-[#e98e6f]",
                backgroundColor: "bg-[#e98e6f]",
              }}
              bookADemoPanel={<BookADemoPanel />}
              className="gap-6"
              formDivClassName="!mx-0"
              leadSourceUTMCampaign="hearOAE launch"
              hearXProductMulti="hearOAE"
              formFooter={
                <div className={styles.disclaimer}>
                  By submitting my details, I hereby give consent to hearX® to
                  process my personal information as provided and contact me,
                  for the purposes of direct marketing, including by means of
                  text message and electronic communication in respect of
                  information and communications related to goods and services
                  offered by hearX®.
                  <a href="https://www.hearxgroup.com/terms">Privacy Policy</a>
                </div>
              }
            />
          </section>
          <section className={styles.disclaimerSection}>
            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              *Disclaimer
              <br /> The intended user population consists of trained personnel,
              such as audiologists, ENT surgeons, medical doctors, hearing
              healthcare professionals or other personnel with a similar level
              of education. This instrument should not be used by personnel who
              do not have the necessary knowledge and training to understand its
              intended use and how the results should be interpreted. hearX does
              not accept any liability regarding any misuse or improper
              application of this instrument by unauthorized personnel.
            </p>
            <h2>LEGAL DISCLAIMER: PRE-ORDER TERMS AND CONDITIONS</h2>
            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              Thank you for your interest in hearOAE.{" "}
            </p>

            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              Prior to placing a pre-order, please carefully review the
              following terms and conditions:
            </p>

            <h3>Pre-Order Availability:</h3>
            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              Pre-orders for the hearOAE device will be available starting April
              17, 2024. Pre-order pricing will be available for a limited time
              only and is subject to be discontinued at any time without prior
              notice.
            </p>

            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              Currently only available in the USA and South Africa.
            </p>

            <h3>Shipping and Delivery:</h3>
            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              Shipments and deliveries of pre-ordered devices are anticipated to
              commence early in 2025. Please note that the order payment must be
              successfully processed and reflect in our records before we
              initiate shipment or delivery of the device.
            </p>

            <h3>Important Information:</h3>
            <ul>
              <li className={classNames(styles.bodyDisclaimer, styles.body)}>
                Pre-order placement does not guarantee immediate availability of
                the device.
              </li>
              <li className={classNames(styles.bodyDisclaimer, styles.body)}>
                The estimated shipping and delivery timeline is subject to
                change, including but not limited to, changes resulting from
                manufacturing capacity or regulatory requirements.
              </li>
              <li className={classNames(styles.bodyDisclaimer, styles.body)}>
                We reserve the right to cancel or modify pre-orders at our
                discretion. In such cases, customers will be notified promptly,
                and any payments made will be refunded in accordance with our
                refund policy.
              </li>
              <li className={classNames(styles.bodyDisclaimer, styles.body)}>
                The final design, specifications, and features of the hearOAE
                device may vary from the information provided at the time of
                pre-order placement.
              </li>
            </ul>

            <h3>Cancellation and Refunds:</h3>
            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              If you wish to cancel your pre-order before the shipment or
              delivery of the device, please contact our customer support team.
              Refunds will be processed in accordance with our refund policy,
              which may vary depending on the timing of the cancellation and
              applicable regulations.
            </p>

            <h3>Limitation of Liability:</h3>
            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              In no event shall hearX Group (Pty) Ltd, its affiliates, partners,
              or licensors be liable for any indirect, incidental, special,
              consequential, or punitive damages, including but not limited to
              loss of profits, revenue, data, or use, incurred by customers or
              third parties arising from the use or inability to use the hearOAE
              device, even if advised of the possibility of such damages.
            </p>

            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              By placing a pre-order for the hearOAE device, you acknowledge and
              agree to these terms and conditions.
            </p>

            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              For further inquiries or assistance, please contact our customer
              <a href="mailto:info@hearxgroup.com">info@hearxgroup.com</a>
            </p>

            <p className={classNames(styles.bodyDisclaimer, styles.body)}>
              <i>Last updated: 08/04/2024</i>
            </p>
          </section>
        </div>
        <FormPopup
          detailsSubmitted={detailsSubmitted}
          successMessage="Your datasheet is ready to download."
          downloadUrl="/assets/documents/hearOAE/CUS_hearOAE_Datasheet_02082024.pdf"
          title="DOWNLOAD DATASHEET"
          subtitle="Submit your details below to download our datasheet."
          openState={downloadDataSheetFormState}
          leadSourceUTMCampaign="hearOAE_Datasheet"
          onAccept={() => setDetailsSubmitted(true)}
          hearXProductMulti="hearOAE"
        />
        <FormPopup
          detailsSubmitted={detailsSubmitted}
          successMessage="Your brochure is ready to download."
          downloadUrl="/assets/documents/hearOAE/CUS_hearOAE Brochure_160424 .pdf"
          title="DOWNLOAD BROCHURE AND PRICING"
          subtitle="Submit your details below to download our product brochure and find out about our pricing structure."
          openState={downloadBrochureFormState}
          leadSourceUTMCampaign="hearOAE launch brochure"
          onAccept={() => setDetailsSubmitted(true)}
          hearXProductMulti="hearOAE"
        />
      </div>
    </Layout>
  );
};

export default HearOaePage;

const playerOptions: Object = {
  width: "100%",
  height: "300px",
  playerVars: {
    // Autoplay + mute has to be activated (value = 1) if you want to autoplay it everywhere
    // Chrome/Safari/Mobile
    // autoplay: 1,
    mute: 0,
    rel: 0,
    showinfo: 0,
    controls: 1,
    disablekb: 1,
    enablejsapi: 1,
    iv_load_policy: 3,
    // For looping video you have to have loop to 1
    // And playlist value equal to your currently playing video
    loop: 1,
    playlist: "-6-AGoxnBSY",
  },
};

const Publication: React.FC<{
  title: ReactNode;
  text: ReactNode;
  href: string;
  image: {
    src: string;
    width: number;
    height: number;
  };
}> = ({ title, text, image, href }) => {
  return (
    <div className={styles.Publication}>
      <a href={href} target="_blank" rel="referrer noopener">
        <Image {...image} />
      </a>
      <div>
        <div className={styles.title}>{title}</div>
        <div className={styles.text}>{text}</div>
        <a href={href} target="_blank" rel="referrer noopener">
          <button>Access Article</button>
        </a>
      </div>
    </div>
  );
};

const BookADemoPanel = () => {
  return (
    <div className={styles.BookADemoPanel}>
      <h3>CONTACT DETAILS</h3>
      <p className={styles.body}>
        Reach out to our team to book a free demo and see hearOAE in action
      </p>
      <ul>
        <li>
          US: <a href="tel:+14152125500">+1 (415) 212 5500</a>
        </li>
        <li>
          RSA: <a href="tel:+270120300268">+27 (0) 12 030 0268</a>
        </li>
      </ul>
      <p className={styles.body}>
        OR Email{" "}
        <a className={styles.emailLink} href="mailto:sales@hearxgroup.com">
          sales@hearxgroup.com
        </a>{" "}
        for purchases.
      </p>

      <h3>OUR OFFICE LOCATIONS</h3>

      <div className={styles.address}>
        <div>United States of America</div>
        <div>Boston office 501 Boylston Street, Floor 10 Boston, MA 02116</div>
      </div>
      <div className={styles.address}>
        <div>South Africa</div>
        <div>
          Pretoria office, Ground Floor, Building 2, Ashlea Gardens Office Park,
          180 Garsfontein Road, Ashlea Gardens, Pretoria, 0081
        </div>
      </div>
    </div>
  );
};

export const WhatsInTheBoxSlide: React.FC<{
  image: string;
  title: string;
  content?: ReactNode;
}> = ({ image, title, content }) => {
  return (
    <div className={styles.whatsInTheBoxSlide}>
      <div className={styles.image}>
        <Image objectFit="contain" src={image} width={512} height={512} />
      </div>
      <div className={styles.content}>
        <h3>{title}</h3>
        {content}
      </div>
    </div>
  );
};
